import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import { bookingApi, ghApi } from "../../api/api";
import { mapBooking } from "../../api/mappers";
import StatusBadge from "./StatusBadge";
import BillCard from "./billing/BillCard";
import {
  checkinTooEarly,
  checkinTooEarlyMsg,
  fmtDate,
  fmtDateAndTime,
  fmtDateTime,
  minutesLeft,
  roomOccupancyState,
} from "./adminHelpers";
import TimeInput12h from "../TimeInput12h";
import useVacantRooms from "../../hooks/useVacantRooms";
import { useBooking } from "../../context/BookingContext";
import {
  DEFAULT_DEPARTURE_TIME,
  addRoomPayloadWindows,
  addRoomWindow,
  extensionWindow,
  splitReusableRooms,
  toStayDateTime,
} from "../../utils/stayWindow";
import AddRoomWindowFields, { isAddRoomWindowValid } from "./AddRoomWindowFields";

const toggleId = (list, id) =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

/* one row per room-assignment inside the status table */
function RoomStatusRow({ a }) {
  /* Shared with the dashboard's Room No chips, so the same room can't read as
     two different states depending on which screen you're on. */
  const state = roomOccupancyState(a);

  const inMaintenance = a.roomStatus === "MAINTENANCE";
  const remaining = inMaintenance ? minutesLeft(a.roomMaintenanceUntil) : null;

  return (
    <Table.Row>
      <Table.Cell>
        <strong>{a.roomNumber}</strong>
      </Table.Cell>
      <Table.Cell>{a.roomType || "—"}</Table.Cell>
      <Table.Cell>
        <Label color={state.color} basic>
          {state.text}
        </Label>
        {inMaintenance && (
          <Label color="orange" basic style={{ marginTop: 4, display: "block" }}>
            <Icon name="wrench" />
            {a.roomMaintenanceRemark || "Under maintenance"}
            {remaining ? ` — ~${remaining} min left` : ""}
          </Label>
        )}
      </Table.Cell>
      <Table.Cell>{fmtDateTime(a.actualCheckin)}</Table.Cell>
      <Table.Cell>{fmtDateTime(a.actualCheckout)}</Table.Cell>
      <Table.Cell>
        {a.hasCustomWindow
          ? fmtDateAndTime(a.effectiveDeparture, a.effectiveDepartureTime)
          : "—"}
      </Table.Cell>
    </Table.Row>
  );
}

export default function FrontDeskModal({
  booking,
  guestHouses = [],
  onClose,
  onOpenBill,
  onUpdated,
}) {
  const [localBooking, setLocalBooking] = useState(booking);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  /* Add Room's own window: from now on for a booking that has already begun,
     so a room freed part-way through the stay is offered (see
     utils/stayWindow addRoomWindow). The same window travels with every added
     room, so the POST checks exactly what the list offered. */
  const [addRoomWin, setAddRoomWin] = useState(() => addRoomWindow(booking));
  useEffect(() => {
    setAddRoomWin(addRoomWindow(localBooking));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBooking.id, localBooking.checkIn, localBooking.arrivalTime,
      localBooking.checkOut, localBooking.departureTime]);
  const { vacancyLoading, refetchVacancy, newRoomOptions } =
    useVacantRooms(guestHouses, localBooking, true, { window: addRoomWin });
  /* Free in the window but refused by the server: already on this booking at
     a house that has not opted in to room re-use. */
  const { kept: addRoomAvailable, hidden: addRoomHidden } =
    splitReusableRooms(newRoomOptions, localBooking, guestHouses);

  /* Opening an indent at the front desk counts as reading it, too. */
  const { markBookingSeen } = useBooking();
  useEffect(() => {
    markBookingSeen(booking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  const [checkinIds, setCheckinIds] = useState([]);
  const [checkinSubmitting, setCheckinSubmitting] = useState(null); /* 'in' | 'noshow' | null */

  const [checkoutIds, setCheckoutIds] = useState([]);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);

  const [extendDate, setExtendDate] = useState("");
  const [extendTime, setExtendTime] = useState("");
  const [extendIds, setExtendIds] = useState([]);
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  /* An assignment id, not a room id: a booking can hold the same room twice. */
  const [changeOldAssignmentId, setChangeOldAssignmentId] = useState("");
  const [changeNewRoomId, setChangeNewRoomId] = useState("");
  const [changeSubmitting, setChangeSubmitting] = useState(false);

  const [addRoomIds, setAddRoomIds] = useState([]);
  const [addRoomSubmitting, setAddRoomSubmitting] = useState(false);

  /* Which guest house's rooms are currently being browsed for Add Room /
     Change Room — shared by both panels' Guest House dropdown. */
  const [viewGhId, setViewGhId] = useState(null);
  const stayGhId = localBooking.stayGuestHouseIds?.[0];
  useEffect(() => {
    if (viewGhId !== null || !guestHouses.length) return;
    /* Open the picker on the house the guest is actually in, which is only the
       house the indent was filed with until a room comes from somewhere else —
       browsing for a second room starts where the first one is. */
    const candidates = [stayGhId, localBooking.guestHouseId]
      .filter(Boolean)
      .map(Number);
    const primary = candidates.find((id) => guestHouses.some((g) => g.id === id));
    setViewGhId(primary ?? guestHouses[0].id);
  }, [guestHouses, stayGhId, localBooking.guestHouseId, viewGhId]);

  const assignments = localBooking.roomAssignments || [];
  const pendingRooms = assignments.filter(
    (a) => !a.actualCheckin && !a.actualCheckout && !a.isNoShow,
  );
  const activeRooms = assignments.filter(
    (a) => a.actualCheckin && !a.actualCheckout && !a.isNoShow,
  );
  const nonCheckedOutRooms = assignments.filter(
    (a) => !a.actualCheckout && !a.isNoShow,
  );

  /* Change Room needs a *different* availability window from Add Room: it is
     checked only as far as the room being vacated departs, which can fall
     earlier than the booking's own end. The instance above is Add Room's, over
     its own window, so this one is separate rather than a narrowing of it.
     Gated on a current room being picked — there is no window without one. */
  const changeRoomAssignment =
    nonCheckedOutRooms.find((a) => a.id === changeOldAssignmentId) || null;
  const {
    vacancyLoading: changeRoomVacancyLoading,
    refetchVacancy: refetchChangeRoomVacancy,
    newRoomOptionsByGh: changeRoomOptionsByGh,
  } = useVacantRooms(guestHouses, localBooking, Boolean(changeRoomAssignment), {
    forAssignment: changeRoomAssignment,
  });
  /* Change Departure only makes sense — and is only exposed to the front desk —
     once at least one room in the booking has actually been checked in. */
  const isCheckedIn = localBooking._apiStatus === "CHECKED_IN";
  /* Change Room is useful earlier than that: between approval and arrival the
     allotted room may still have to move, and the backend accepts change-room
     for CONFIRMED bookings as well as CHECKED_IN ones. */
  const canChangeRoom = ["CONFIRMED", "CHECKED_IN"].includes(localBooking._apiStatus);

  /* Reset selections to sensible defaults whenever the underlying booking
     state changes (initial load or after any action refetch). */
  useEffect(() => {
    setCheckinIds(
      pendingRooms.filter((a) => a.roomStatus !== "MAINTENANCE").map((a) => a.id),
    );
    setCheckoutIds(activeRooms.map((a) => a.id));
    setExtendIds(activeRooms.map((a) => a.id));
    if (!extendDate) {
      /* Start from the latest current departure, so it can be moved either way. */
      const latest = [...activeRooms]
        .sort((x, y) =>
          `${x.effectiveDeparture}T${x.effectiveDepartureTime}`.localeCompare(
            `${y.effectiveDeparture}T${y.effectiveDepartureTime}`,
          ),
        )
        .pop();
      setExtendDate(latest?.effectiveDeparture || localBooking.checkOut || "");
      setExtendTime((latest?.effectiveDepartureTime || localBooking.departureTime || "").slice(0, 5));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBooking]);

  const refresh = async () => {
    const full = await bookingApi.get(localBooking.id);
    const mapped = mapBooking(full);
    setLocalBooking(mapped);
    onUpdated?.(mapped);
    refetchVacancy();
    refetchChangeRoomVacancy();
    return mapped;
  };

  const runAction = async (fn, successMsg) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      await refresh();
      setNotice(successMsg);
    } catch (e) {
      setError(e.message || "Action failed.");
      if (e.code === "ALREADY_DONE" || e.code === "ROOM_IN_MAINTENANCE" || e.code === "ROOM_OCCUPIED") {
        /* Someone else already did this, a room's maintenance state changed
           since we loaded, or the room turned out to still be held by
           another live booking — pull the current state so the admin sees
           what actually happened instead of a stale view. */
        await refresh();
      }
    }
  };

  const handleCheckin = async (noShow) => {
    if (!checkinIds.length) return;
    /* Marking a no-show is exempt — it releases the room instead of
       occupying it, so it stays available before the arrival date. */
    const early = noShow ? [] : checkinTooEarly(
      localBooking, assignments.filter((a) => checkinIds.includes(a.id)),
    );
    if (early.length) {
      setNotice(null);
      setError(checkinTooEarlyMsg(localBooking, early));
      return;
    }
    setCheckinSubmitting(noShow ? "noshow" : "in");
    await runAction(
      () => bookingApi.checkin(localBooking.id, checkinIds, noShow),
      noShow ? "Room(s) marked as no-show." : "Checked in successfully.",
    );
    setCheckinSubmitting(null);
  };

  const handleCheckout = async () => {
    setCheckoutSubmitting(true);
    await runAction(
      () => bookingApi.checkout(localBooking.id, checkoutIds),
      "Checked out successfully.",
    );
    setCheckoutSubmitting(false);
  };

  const handleExtend = async () => {
    if (!extendDate || !extendTime) {
      setError("Select the new departure date and time.");
      return;
    }
    setError(null);
    setExtendSubmitting(true);

    /* Only rooms moving later can collide with another stay, so only they are
       pre-checked — each over the window from its own current departure to
       the new one, in its own guest house (a booking's rooms can span several).
       The booking is excluded from its own check: the window starts exactly at
       the checkout it currently holds, so otherwise each room conflicts with
       itself. An earlier departure is validated by the server alone. */
    try {
      const newEnd = toStayDateTime(extendDate, extendTime, DEFAULT_DEPARTURE_TIME);
      const currentDate = (a) => a.effectiveDeparture || localBooking.checkOut;
      const currentTime = (a) => a.effectiveDepartureTime || localBooking.departureTime;
      const later = activeRooms.filter(
        (a) =>
          extendIds.includes(a.id) &&
          newEnd > toStayDateTime(currentDate(a), currentTime(a), DEFAULT_DEPARTURE_TIME),
      );
      const free = await Promise.all(
        later.map((a) => {
          const { start, end } = extensionWindow(
            localBooking, currentDate(a), extendDate, currentTime(a), extendTime,
          );
          /* Exempt only this stay — a sibling stay of the same booking on the
             same room is a real conflict now that a room can be re-seated. */
          return ghApi
            .availableRooms(a.guestHouseId, start, end, { excludeAssignment: a.id })
            .then((data) => (data.rooms || []).some((r) => Number(r.id) === Number(a.roomId)));
        }),
      );
      const unavailable = later.filter((_, i) => !free[i]);
      if (unavailable.length) {
        setError(
          `Room(s) ${unavailable.map((a) => a.roomNumber).join(", ")} are not available until ${fmtDateAndTime(extendDate, extendTime)}.`,
        );
        setExtendSubmitting(false);
        return;
      }
    } catch (e) {
      setError(e.message || "Failed to check room availability.");
      setExtendSubmitting(false);
      return;
    }

    await runAction(
      () => bookingApi.extend(localBooking.id, extendDate, extendIds, extendTime),
      "Departure updated.",
    );
    setExtendSubmitting(false);
  };

  const handleChangeRoom = async () => {
    if (!changeOldAssignmentId || !changeNewRoomId) {
      setError("Select both the current room and the new room.");
      return;
    }
    setChangeSubmitting(true);
    await runAction(
      () => bookingApi.changeRoom(
        localBooking.id, changeOldAssignmentId, changeNewRoomId, changeRoomAssignment?.roomId,
      ),
      "Room changed successfully.",
    );
    setChangeSubmitting(false);
    setChangeOldAssignmentId("");
    setChangeNewRoomId("");
  };

  const handleAddRoom = async () => {
    if (!addRoomIds.length) {
      setError("Select at least one room to add.");
      return;
    }
    setAddRoomSubmitting(true);
    await runAction(
      () => bookingApi.addRoom(
        localBooking.id, addRoomIds, addRoomPayloadWindows(addRoomIds, addRoomWin, localBooking),
      ),
      addRoomIds.length > 1 ? "Rooms added successfully." : "Room added successfully.",
    );
    setAddRoomSubmitting(false);
    setAddRoomIds([]);
    setAddRoomWin(addRoomWindow(localBooking));
  };

  const viewGhNewRoomOptions = addRoomAvailable.filter((r) => r.guestHouseId === viewGhId);
  /* Change Room browses the same guest house as Add Room, but off its own,
     room-scoped availability list. */
  const changeRoomNewOptions = changeRoomOptionsByGh.get(viewGhId) || [];

  const ghDropdownOptions = guestHouses.map((gh) => ({ key: gh.id, value: gh.id, text: gh.name }));
  const ghDropdownProps = {
    selection: true,
    search: true,
    fluid: true,
    placeholder: "— Select Guest House —",
    options: ghDropdownOptions,
    value: viewGhId || "",
    onChange: (_, data) => setViewGhId(data.value ? Number(data.value) : null),
  };

  /* Rooms already picked (Add Room) from a different guest house than the
     one currently shown still need to be included as options — otherwise
     Semantic UI's multi-select Dropdown would drop their chip from view
     (and silently lose them from `value`) the moment the guest house
     dropdown is switched. */
  const addRoomDropdownOptions = [
    ...viewGhNewRoomOptions,
    ...newRoomOptions.filter((r) => addRoomIds.includes(r.id) && r.guestHouseId !== viewGhId),
  ];

  return (
    <Modal open size="large" onClose={onClose} closeIcon>
      <Modal.Header>
        <Icon name="bed" />
        Front Desk — {localBooking.bookingCode}
        <span style={{ float: "right" }}>
          <StatusBadge status={localBooking.status} />
        </span>
      </Modal.Header>
      <Modal.Content scrolling>
        <Message info>
          <Label.Group>
            <Label basic>
              <Icon name="user outline" />
              {localBooking.guestName || localBooking.applicantName}
            </Label>
            <Label basic>
              <Icon name="building outline" />
              {/* The house(s) actually holding a room, falling back to the
                  filed house — the desk needs to read where the guest sleeps,
                  not which house's queue the indent came through. */}
              {(localBooking.stayGuestHouseNames || []).join(", ") ||
                localBooking.guestHouseName}
            </Label>
            <Label basic>
              <Icon name="calendar alternate outline" />
              {fmtDate(localBooking.checkIn)} → {fmtDate(localBooking.checkOut)}
            </Label>
          </Label.Group>
        </Message>

        {error && (
          <Message negative onDismiss={() => setError(null)}>
            <Icon name="exclamation triangle" />
            {error}
          </Message>
        )}
        {notice && (
          <Message positive onDismiss={() => setNotice(null)}>
            <Icon name="check circle" />
            {notice}
          </Message>
        )}

        {/* ── Room status overview ── */}
        <Segment>
          <Header as="h5" icon="home" content="Rooms" />
          <Table celled compact size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Room</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Checked In</Table.HeaderCell>
                <Table.HeaderCell>Checked Out</Table.HeaderCell>
                <Table.HeaderCell>Changed Departure</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {assignments.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center">
                    No rooms assigned.
                  </Table.Cell>
                </Table.Row>
              ) : (
                assignments.map((a) => <RoomStatusRow key={a.id} a={a} />)
              )}
            </Table.Body>
          </Table>
        </Segment>

        {/* ── Check In ── */}
        {pendingRooms.length > 0 && (
          <Segment>
            <Header as="h5" icon="sign in" content="Check In" />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {pendingRooms.map((a) => {
                const inMaintenance = a.roomStatus === "MAINTENANCE";
                const remaining = inMaintenance ? minutesLeft(a.roomMaintenanceUntil) : null;
                return (
                  <div key={a.id}>
                    <Checkbox
                      label={`Room ${a.roomNumber} — ${a.roomType || "—"}`}
                      checked={checkinIds.includes(a.id)}
                      disabled={inMaintenance}
                      onChange={() => setCheckinIds((prev) => toggleId(prev, a.id))}
                    />
                    {inMaintenance && (
                      <Label color="orange" basic size="tiny" style={{ marginLeft: 8 }}>
                        <Icon name="wrench" />
                        {a.roomMaintenanceRemark || "Under maintenance"}
                        {remaining ? ` — ~${remaining} min left` : ""}
                      </Label>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              color="blue"
              disabled={!checkinIds.length}
              loading={checkinSubmitting === "in"}
              onClick={() => handleCheckin(false)}
              type="button"
            >
              <Icon name="sign in" />
              Check In Selected
            </Button>
            <Button
              basic
              color="orange"
              disabled={!checkinIds.length}
              loading={checkinSubmitting === "noshow"}
              onClick={() => handleCheckin(true)}
              type="button"
            >
              <Icon name="user times" />
              Mark No-Show
            </Button>
          </Segment>
        )}

        {/* ── Check Out ── */}
        {activeRooms.length > 0 && (
          <Segment>
            <Header as="h5" icon="sign out" content="Check Out" />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {activeRooms.map((a) => (
                <Checkbox
                  key={a.id}
                  label={`Room ${a.roomNumber} — ${a.roomType || "—"} (checked in ${fmtDateTime(a.actualCheckin)})`}
                  checked={checkoutIds.includes(a.id)}
                  onChange={() => setCheckoutIds((prev) => toggleId(prev, a.id))}
                />
              ))}
            </div>
            <Button
              color="grey"
              disabled={!checkoutIds.length}
              loading={checkoutSubmitting}
              onClick={handleCheckout}
              type="button"
            >
              <Icon name="sign out" />
              Check Out Selected
            </Button>
          </Segment>
        )}

        {/* ── Bill: what is still owed, right where the guest leaves. Shown only
            to roles that may see bills (night staff do not), and never in the
            way of the check-out itself. ── */}
        <BillCard booking={localBooking} onOpenBill={onOpenBill} />

        {/* ── Change Departure (visible only once checked in) ── */}
        {isCheckedIn && activeRooms.length > 0 && (
          <Segment>
            <Header as="h5" icon="calendar alternate outline" content="Change Departure" />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {activeRooms.map((a) => (
                <Checkbox
                  key={a.id}
                  label={`Room ${a.roomNumber} — currently until ${fmtDateAndTime(
                    a.effectiveDeparture || localBooking.checkOut,
                    a.effectiveDepartureTime || localBooking.departureTime,
                  )}`}
                  checked={extendIds.includes(a.id)}
                  onChange={() => setExtendIds((prev) => toggleId(prev, a.id))}
                />
              ))}
            </div>
            <Form>
              <Form.Group>
                <Form.Input
                  label="New Departure Date"
                  type="date"
                  value={extendDate}
                  min={localBooking.checkIn || undefined}
                  onChange={(e) => setExtendDate(e.target.value)}
                />
                <TimeInput12h label="New Departure Time" value={extendTime} onChange={setExtendTime} />
              </Form.Group>
            </Form>
            <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
              Move it later or earlier. An earlier departure must still be after the guest checked in.
            </p>
            <Button
              color="violet"
              disabled={!extendIds.length || !extendDate || !extendTime}
              loading={extendSubmitting}
              onClick={handleExtend}
              type="button"
            >
              <Icon name="calendar alternate outline" />
              Change Departure
            </Button>
          </Segment>
        )}

        {/* ── Add Room (one or more at once, across any authorized guest house) ── */}
        <Segment>
          <Header as="h5" icon="add square outline" content="Add Room" />
          <AddRoomWindowFields
            booking={localBooking}
            value={addRoomWin}
            onChange={setAddRoomWin}
            hiddenRooms={addRoomHidden}
          />
          <Form>
            <Form.Field>
              <label>Guest House</label>
              <Dropdown {...ghDropdownProps} />
            </Form.Field>
            <Form.Select
              multiple
              label="Room(s)"
              loading={vacancyLoading}
              placeholder={
                vacancyLoading
                  ? "Checking availability…"
                  : addRoomDropdownOptions.length
                    ? "— Select room(s) —"
                    : "No vacant rooms in this guest house"
              }
              disabled={vacancyLoading || !viewGhId}
              options={addRoomDropdownOptions.map((r) => ({
                key: r.id,
                value: r.id,
                text: `${r.guestHouseName} — Room ${r.roomNumber} (${r.roomType})`,
              }))}
              value={addRoomIds}
              onChange={(_, data) => setAddRoomIds(data.value)}
            />
          </Form>
          <Button
            color="green"
            disabled={!addRoomIds.length || !isAddRoomWindowValid(addRoomWin)}
            loading={addRoomSubmitting}
            onClick={handleAddRoom}
            type="button"
          >
            <Icon name="add square outline" />
            {addRoomIds.length > 1 ? `Add ${addRoomIds.length} Rooms` : "Add Room"}
          </Button>
        </Segment>

        {/* ── Change Room (visible once the booking is approved) ── */}
        {canChangeRoom && nonCheckedOutRooms.length > 0 && (
          <Segment>
            <Header as="h5" icon="exchange" content="Change Room" />
            <Form>
              <Form.Select
                label="Current Room"
                placeholder="— Select current room —"
                options={nonCheckedOutRooms.map((a) => ({
                  key: a.id,
                  value: a.id,
                  text: `Room ${a.roomNumber} — ${a.roomType || "—"}`,
                }))}
                value={changeOldAssignmentId || ""}
                onChange={(_, data) => {
                  setChangeOldAssignmentId(data.value);
                  /* The window changes with the room, so a room picked under
                     the previous one may no longer be on offer. */
                  setChangeNewRoomId("");
                }}
              />
            </Form>
            <Form>
              <Form.Field>
                <label>New Room — Guest House</label>
                <Dropdown {...ghDropdownProps} />
              </Form.Field>
              <Form.Select
                label="New Room"
                search
                loading={changeRoomVacancyLoading}
                placeholder={
                  !changeRoomAssignment
                    ? "Select the current room first"
                    : changeRoomVacancyLoading
                      ? "Checking availability…"
                      : changeRoomNewOptions.length
                        ? "— Select new room —"
                        : "No vacant rooms in this guest house"
                }
                disabled={
                  !changeRoomAssignment ||
                  changeRoomVacancyLoading ||
                  !changeRoomNewOptions.length
                }
                options={changeRoomNewOptions.map((r) => ({
                  key: r.id,
                  value: r.id,
                  text: `Room ${r.roomNumber} (${r.roomType})`,
                }))}
                value={changeNewRoomId || ""}
                onChange={(_, data) => setChangeNewRoomId(data.value)}
              />
            </Form>
            <Button
              color="blue"
              disabled={!changeOldAssignmentId || !changeNewRoomId}
              loading={changeSubmitting}
              onClick={handleChangeRoom}
              type="button"
            >
              <Icon name="exchange" />
              Change Room
            </Button>
          </Segment>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={onClose} type="button">
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
